import { resolveClient } from './supabase';
import type {
  Node,
  Edge,
  CreateNodeInput,
  UpdateNodeInput,
  CreateEdgeInput,
  UpdateEdgeInput,
  NodeFilter,
  EdgeFilter,
  PaginationOptions,
  NodeWithRelationships,
  PaginatedResponse,
  FilterModel,
  ColumnFilter,
} from '@lingaai/types';
import { NodeSchemaRegistry, validateNodeProps, isValidSourceType, isValidTargetType, extractNaturalKey } from '@lingaai/types';
import { z } from 'zod';
import type { SupabaseClient } from '@supabase/supabase-js';
import { isNodeTypeAllowed, isEdgeTypeAllowed, getTenantModuleConfig } from './modules';
import { handleSupabaseError, handleValidationError, createPermissionError, createEdgeValidationError, isGraphError, GraphErrorCode, GraphNotFoundError } from './errors';

// ============================================
// FILTER HELPERS
// ============================================

/**
 * Build a single filter condition string for .or() clause
 */
function buildFilterCondition(filter: ColumnFilter): string | null {
  const { field, operator, value } = filter;

  // Determine if field is in props or top-level
  const systemFields = ['id', 'type', 'created_at', 'updated_at', 'deleted_at'];
  const fieldPath = systemFields.includes(field) ? field : `props->>${field}`;

  switch (operator) {
    case 'is':
    case 'equals':
      return `${fieldPath}.eq.${value}`;
    case 'not':
      return `${fieldPath}.neq.${value}`;
    case 'contains':
      return `${fieldPath}.ilike.%${value}%`;
    case 'startsWith':
      return `${fieldPath}.ilike.${value}%`;
    case 'endsWith':
      return `${fieldPath}.ilike.%${value}`;
    case 'isEmpty':
      return `${fieldPath}.is.null`;
    case 'isNotEmpty':
      return `${fieldPath}.not.is.null`;
    case 'isAnyOf':
      if (Array.isArray(value) && value.length > 0) {
        return `${fieldPath}.in.(${value.join(',')})`;
      }
      return null;
    case '>':
      return `${fieldPath}.gt.${value}`;
    case '>=':
      return `${fieldPath}.gte.${value}`;
    case '<':
      return `${fieldPath}.lt.${value}`;
    case '<=':
      return `${fieldPath}.lte.${value}`;
    default:
      return null;
  }
}

/**
 * Apply a single filter to query (for AND logic)
 */
function applyFilter(query: any, filter: ColumnFilter) {
  const { field, operator, value } = filter;

  const systemFields = ['id', 'type', 'created_at', 'updated_at', 'deleted_at'];
  const fieldPath = systemFields.includes(field) ? field : `props->>${field}`;

  switch (operator) {
    case 'is':
    case 'equals':
      return query.eq(fieldPath, value);
    case 'not':
      return query.neq(fieldPath, value);
    case 'contains':
      return query.ilike(fieldPath, `%${value}%`);
    case 'startsWith':
      return query.ilike(fieldPath, `${value}%`);
    case 'endsWith':
      return query.ilike(fieldPath, `%${value}`);
    case 'isEmpty':
      return query.is(fieldPath, null);
    case 'isNotEmpty':
      return query.not(fieldPath, 'is', null);
    case 'isAnyOf':
      if (Array.isArray(value) && value.length > 0) {
        return query.in(fieldPath, value);
      }
      return query;
    case '>':
      return query.gt(fieldPath, value);
    case '>=':
      return query.gte(fieldPath, value);
    case '<':
      return query.lt(fieldPath, value);
    case '<=':
      return query.lte(fieldPath, value);
    default:
      return query;
  }
}

/**
 * Build filter clauses for Supabase query
 */
function buildFilterClauses(query: any, filterModel: FilterModel | undefined, logicOperator: 'and' | 'or' = 'and') {
  if (!filterModel?.items?.length) {
    return query;
  }

  console.log('🔧 Building filter clauses:', { filterModel, logicOperator });

  // For 'or' logic, we need to use .or() with a string
  // For 'and' logic, we chain individual filters
  if (logicOperator === 'or') {
    const orConditions: string[] = [];

    for (const filter of filterModel.items) {
      const condition = buildFilterCondition(filter);
      console.log('  OR condition:', { filter, condition });
      if (condition) orConditions.push(condition);
    }

    if (orConditions.length > 0) {
      console.log('  Applying OR:', orConditions.join(','));
      query = query.or(orConditions.join(','));
    }
  } else {
    // AND logic - chain filters
    for (const filter of filterModel.items) {
      console.log('  AND filter:', filter);
      query = applyFilter(query, filter);
    }
  }

  return query;
}

// ============================================
// NODE OPERATIONS
// ============================================

/**
 * Get all nodes with optional filtering and pagination
 */
export async function getNodes(filter?: NodeFilter, pagination?: PaginationOptions, client?: SupabaseClient): Promise<Node[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('nodes').select('*');

  // Apply filters
  if (filter?.type) {
    query = query.eq('type', filter.type);
  }

  if (filter?.natural_key) {
    query = query.eq('natural_key', filter.natural_key);
  }

  if (filter?.props) {
    // Search within JSONB props
    Object.entries(filter.props).forEach(([key, value]) => {
      query = query.contains('props', { [key]: value });
    });
  }

  // Soft delete filtering
  if (filter?.deleted === false || filter?.deleted === undefined) {
    query = query.is('deleted_at', null);
  }

  // Apply pagination
  if (pagination?.orderBy) {
    const ascending = pagination.orderDirection !== 'desc';
    query = query.order(pagination.orderBy, { ascending });
  }

  if (pagination?.limit) {
    query = query.limit(pagination.limit);
  }

  if (pagination?.offset) {
    query = query.range(pagination.offset, pagination.offset + (pagination.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'list nodes',
      entityType: 'node',
    });
  }
  return data as Node[];
}

/**
 * List nodes with pagination, filtering, and sorting
 * Returns paginated response with metadata
 */
export async function listNodes(
  type: string,
  filters?: {
    search?: string;
    includeDeleted?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filterModel?: FilterModel;
    includes?: Array<{
      edgeType: string;
      direction: 'outgoing' | 'incoming';
      nodeTypes: string[];
      as: string;
      includeEdgeProps?: boolean;
      edgeFilter?: Record<string, unknown>;
    }>;
  },
  client?: SupabaseClient
): Promise<PaginatedResponse<Node>> {
  const supabase = resolveClient(client);

  const page = filters?.page || 1;
  const pageSize = filters?.pageSize || 50;
  const sortBy = filters?.sortBy || 'created_at';
  const sortOrder = filters?.sortOrder || 'desc';

  const offset = (page - 1) * pageSize;

  let query = supabase.from('nodes').select('*', { count: 'exact' }).eq('type', type);

  if (!filters?.includeDeleted) {
    query = query.is('deleted_at', null);
  }

  // Apply column filters BEFORE search
  if (filters?.filterModel) {
    query = buildFilterClauses(query, filters.filterModel, filters.filterModel.linkOperator || 'and');
  }

  if (filters?.search) {
    // Use RPC for graph search - searches props and connected AMFI nodes
    const { data: searchResults, error: searchError } = await supabase.rpc('search_graph_nodes', {
      search_term: filters.search,
      node_type: type,
      page_num: page,
      page_size: pageSize,
    });
    if (searchError) {
      handleSupabaseError(searchError, {
        operation: 'search graph nodes',
        entityType: 'node',
        nodeType: type,
      });
    }
    return {
      data: (searchResults || []) as Node[],
      pagination: {
        total: searchResults?.length || 0,
        page,
        pageSize,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: page > 1,
      },
    };
  }

  if (sortBy.startsWith('props.')) {
    const propKey = sortBy.replace('props.', '');
    query = query.order(`props->>${propKey}`, { ascending: sortOrder === 'asc' });
  } else {
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });
  }

  query = query.range(offset, offset + pageSize - 1);

  const { data, error, count } = await query;
  if (error) {
    handleSupabaseError(error, {
      operation: 'list nodes with pagination',
      entityType: 'node',
      nodeType: type,
    });
  }

  const total = count || 0;
  const totalPages = Math.ceil(total / pageSize);

  let nodesWithIncludes = data as Node[];

  // Process includes if provided
  if (filters?.includes && filters.includes.length > 0 && data && data.length > 0) {
    const nodeIds = data.map((n) => n.id);

    // Fetch included data for each include config
    for (const include of filters.includes) {
      const { edgeType, direction, nodeTypes, as, includeEdgeProps, edgeFilter } = include;

      // Build query to fetch related nodes
      let edgeQuery = supabase
        .from('edges')
        .select('id, src, dst, type, props, target:nodes!edges_dst_fkey(id, type, props)')
        .eq('type', edgeType)
        .in(direction === 'outgoing' ? 'src' : 'dst', nodeIds)
        .is('deleted_at', null);

      // Filter by target node types
      if (nodeTypes.length > 0) {
        edgeQuery = edgeQuery.in('target.type', nodeTypes);
      }

      // Apply edge property filters if provided
      if (edgeFilter) {
        for (const [key, value] of Object.entries(edgeFilter)) {
          edgeQuery = edgeQuery.eq(`props->>${key}`, value);
        }
      }

      const { data: edgeData, error: edgeError } = await edgeQuery;

      if (edgeError) {
        console.error('Error fetching includes:', edgeError);
        continue;
      }

      // Attach included data to nodes
      nodesWithIncludes = nodesWithIncludes.map((node) => {
        const relatedEdges = edgeData?.filter((e: any) => (direction === 'outgoing' ? e.src === node.id : e.dst === node.id));

        if (!relatedEdges || relatedEdges.length === 0) {
          return node;
        }

        // Get the first related node (for single relationships)
        const relatedEdge = relatedEdges[0] as any;
        const relatedNode = relatedEdge?.target;

        if (!relatedNode) {
          return node;
        }

        // Attach included data
        const includeData: any = {
          id: relatedNode.id,
          type: relatedNode.type,
          props: relatedNode.props,
        };

        // Attach edge properties if requested
        if (includeEdgeProps && relatedEdge.props) {
          includeData._edge = relatedEdge.props;
        }

        return {
          ...node,
          _includes: {
            ...((node as any)._includes || {}),
            [as]: includeData,
          },
        } as Node;
      });
    }
  }

  return {
    data: nodesWithIncludes,
    pagination: {
      total,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    },
  };
}

/**
 * Get a single node by ID
 */
export async function getNode(nodeId: string, client?: SupabaseClient): Promise<Node> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('nodes').select('*').eq('id', nodeId).single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'get node',
      entityType: 'node',
    });
  }
  return data as Node;
}

/**
 * Get node by natural key
 */
export async function getNodeByNaturalKey(tenantId: string, type: string, naturalKey: string, client?: SupabaseClient): Promise<Node | null> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('nodes').select('*').eq('tenant_id', tenantId).eq('type', type).eq('natural_key', naturalKey).is('deleted_at', null).maybeSingle();

  if (error) {
    handleSupabaseError(error, {
      operation: 'get node by natural key',
      entityType: 'node',
      nodeType: type,
    });
  }
  return data as Node | null;
}

/**
 * Create a new node with Zod validation and module permission check
 *
 * @param input - Node data to create
 * @param options - Optional configuration
 * @param options.client - Supabase client instance
 * @param options.skipModuleCheck - Skip manual module permission check (frontend already validated)
 *                                   Note: Database constraint still enforces permissions for security
 */
export async function createNode<TProps = Record<string, unknown>>(
  input: CreateNodeInput<TProps>,
  options?: {
    client?: SupabaseClient;
    skipModuleCheck?: boolean;
  }
): Promise<Node> {
  const supabase = resolveClient(options?.client);

  // Manual module permission check (can be skipped if frontend already validated)
  // Database constraint provides security layer regardless
  if (!options?.skipModuleCheck) {
    const moduleConfig = await getTenantModuleConfig(input.tenant_id, options?.client);
    if (!isNodeTypeAllowed(input.type, moduleConfig)) {
      createPermissionError('node', input.type);
    }
  }

  // Validate props using Zod schema if available
  const schema = NodeSchemaRegistry[input.type];
  if (schema) {
    const validation = validateNodeProps(schema, input.props);
    if (!validation.success) {
      handleValidationError(validation.errors.errors, { nodeType: input.type });
    }
  }

  // Auto-populate natural_key from props if not provided
  const nodeInput = {
    ...input,
    natural_key: input.natural_key ?? extractNaturalKey(input.type, input.props as Record<string, unknown>),
  };

  const { data, error } = await supabase.from('nodes').insert(nodeInput).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'create node',
      entityType: 'node',
      nodeType: input.type,
    });
  }

  return data as Node;
}

/**
 * Update a node with optional Zod validation
 * Props are MERGED with existing props, not replaced
 */
export async function updateNode<TProps = Record<string, unknown>>(nodeId: string, updates: UpdateNodeInput<TProps>, nodeType?: string, client?: SupabaseClient): Promise<Node> {
  const supabase = resolveClient(client);

  console.log('🔧 updateNode START', { nodeId, nodeType, updateProps: updates.props });

  // Validate props using Zod schema if nodeType is provided and schema exists
  // Use .partial() for updates since we're only validating the fields being updated, not the full object
  if (nodeType && updates.props) {
    const schema = NodeSchemaRegistry[nodeType];
    console.log('🔍 Schema check', { nodeType, hasSchema: !!schema, hasPartial: schema && 'partial' in schema });

    if (schema && 'partial' in schema) {
      const partialSchema = (schema as z.ZodObject<z.ZodRawShape>).partial();
      console.log('✅ Validating partial props', { props: updates.props });
      const validation = validateNodeProps(partialSchema, updates.props);

      if (!validation.success) {
        console.error('❌ Validation FAILED on partial props', { errors: validation.errors.errors });
        handleValidationError(validation.errors.errors, { nodeType });
      }
      console.log('✅ Validation PASSED on partial props');
    }
  }

  // If updating props, merge with existing props instead of replacing
  let finalUpdates = updates;
  if (updates.props) {
    console.log('🔄 Fetching existing props for merge');
    const { data: existing } = await supabase.from('nodes').select('props').eq('id', nodeId).single();
    console.log('📦 Existing props', { existing: existing?.props });

    if (existing) {
      finalUpdates = {
        ...updates,
        props: { ...existing.props, ...updates.props },
      };
      console.log('🔀 Merged props', { merged: finalUpdates.props });
    }
  }

  console.log('💾 Sending to database', { finalUpdates });
  const { data, error } = await supabase.from('nodes').update(finalUpdates).eq('id', nodeId).select().single();

  if (error) {
    console.error('❌ Database update FAILED', { error, errorCode: error.code, errorMessage: error.message, errorDetails: error.details });
    handleSupabaseError(error, {
      operation: 'update node',
      entityType: 'node',
      nodeType,
    });
  }

  console.log('✅ updateNode SUCCESS', { nodeId, updatedProps: data?.props });
  return data as Node;
}

/**
 * Soft delete a node (sets deleted_at timestamp)
 */
export async function softDeleteNode(nodeId: string, client?: SupabaseClient): Promise<Node> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('nodes').update({ deleted_at: new Date().toISOString() }).eq('id', nodeId).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'soft delete node',
      entityType: 'node',
    });
  }
  return data as Node;
}

/**
 * Permanently delete a node
 */
export async function deleteNode(nodeId: string, client?: SupabaseClient): Promise<void> {
  const supabase = resolveClient(client);

  const { error } = await supabase.from('nodes').delete().eq('id', nodeId);

  if (error) {
    handleSupabaseError(error, {
      operation: 'delete node',
      entityType: 'node',
    });
  }
}

/**
 * Restore a soft-deleted node
 */
export async function restoreNode(nodeId: string, client?: SupabaseClient): Promise<Node> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('nodes').update({ deleted_at: null }).eq('id', nodeId).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'restore node',
      entityType: 'node',
    });
  }
  return data as Node;
}

/**
 * Upsert a node (insert or update based on natural_key)
 * Includes Zod schema validation when schema exists for the node type
 */
export async function upsertNode<TProps = Record<string, unknown>>(input: CreateNodeInput<TProps>, client?: SupabaseClient): Promise<Node> {
  const supabase = resolveClient(client);

  // Validate props using Zod schema if available
  const schema = NodeSchemaRegistry[input.type];
  if (schema) {
    const validation = validateNodeProps(schema, input.props);
    if (!validation.success) {
      handleValidationError(validation.errors.errors, { nodeType: input.type });
    }
  }

  // Upsert based on unique constraint (tenant_id, type, natural_key)
  const { data, error } = await supabase
    .from('nodes')
    .upsert(input, {
      onConflict: 'tenant_id,type,natural_key',
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'upsert node',
      entityType: 'node',
      nodeType: input.type,
    });
  }
  return data as Node;
}

// ============================================
// EDGE OPERATIONS
// ============================================

/**
 * Get all edges with optional filtering and pagination
 */
export async function getEdges(filter?: EdgeFilter, pagination?: PaginationOptions, client?: SupabaseClient): Promise<Edge[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('edges').select('*');

  // Apply filters
  if (filter?.type) {
    query = query.eq('type', filter.type);
  }

  if (filter?.src) {
    query = query.eq('src', filter.src);
  }

  if (filter?.dst) {
    query = query.eq('dst', filter.dst);
  }

  if (filter?.props) {
    // Search within JSONB props
    Object.entries(filter.props).forEach(([key, value]) => {
      query = query.contains('props', { [key]: value });
    });
  }

  // Soft delete filtering
  if (filter?.deleted === false || filter?.deleted === undefined) {
    query = query.is('deleted_at', null);
  }

  // Apply pagination
  if (pagination?.orderBy) {
    const ascending = pagination.orderDirection !== 'desc';
    query = query.order(pagination.orderBy, { ascending });
  }

  if (pagination?.limit) {
    query = query.limit(pagination.limit);
  }

  if (pagination?.offset) {
    query = query.range(pagination.offset, pagination.offset + (pagination.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'list edges',
      entityType: 'edge',
    });
  }
  return data as Edge[];
}

/**
 * Get a single edge by ID
 */
export async function getEdge(edgeId: string, client?: SupabaseClient): Promise<Edge> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('edges').select('*').eq('id', edgeId).single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'get edge',
      entityType: 'edge',
    });
  }
  return data as Edge;
}

/**
 * Create a new edge with module permission check and source/target validation
 *
 * @param input - Edge data to create
 * @param options - Optional configuration
 * @param options.client - Supabase client instance
 * @param options.skipModuleCheck - Skip manual module permission check (frontend already validated)
 */
export async function createEdge<TProps = Record<string, unknown>>(
  input: CreateEdgeInput<TProps>,
  options?: {
    client?: SupabaseClient;
    skipModuleCheck?: boolean;
  }
): Promise<Edge> {
  const supabase = resolveClient(options?.client);

  // Manual module permission check (can be skipped if frontend already validated)
  if (!options?.skipModuleCheck) {
    const moduleConfig = await getTenantModuleConfig(input.tenant_id, options?.client);
    if (!isEdgeTypeAllowed(input.type, moduleConfig)) {
      createPermissionError('edge', input.type);
    }
  }

  // Validate source/target node types
  const { data: srcNode } = await supabase.from('nodes').select('type').eq('id', input.src).single();
  const { data: dstNode } = await supabase.from('nodes').select('type').eq('id', input.dst).single();

  if (srcNode && !isValidSourceType(input.type, srcNode.type)) {
    createEdgeValidationError(input.type, srcNode.type, 'source');
  }
  if (dstNode && !isValidTargetType(input.type, dstNode.type)) {
    createEdgeValidationError(input.type, dstNode.type, 'target');
  }

  const { data, error } = await supabase.from('edges').insert(input).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'create edge',
      entityType: 'edge',
      edgeType: input.type,
    });
  }

  return data as Edge;
}

/**
 * Update an edge
 */
export async function updateEdge<TProps = Record<string, unknown>>(edgeId: string, updates: UpdateEdgeInput<TProps>, client?: SupabaseClient): Promise<Edge> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('edges').update(updates).eq('id', edgeId).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'update edge',
      entityType: 'edge',
    });
  }
  return data as Edge;
}

/**
 * Upsert an edge (create or update if exists)
 * Useful for idempotent edge creation
 */
export async function upsertEdge<TProps = Record<string, unknown>>(input: CreateEdgeInput<TProps>, client?: SupabaseClient): Promise<Edge> {
  const supabase = resolveClient(client);

  // Get module config and check if edge type is allowed
  const moduleConfig = await getTenantModuleConfig(input.tenant_id, client);
  if (!isEdgeTypeAllowed(input.type, moduleConfig)) {
    createPermissionError('edge', input.type);
  }

  // Validate source/target node types
  const { data: srcNode } = await supabase.from('nodes').select('type').eq('id', input.src).single();
  const { data: dstNode } = await supabase.from('nodes').select('type').eq('id', input.dst).single();

  if (srcNode && !isValidSourceType(input.type, srcNode.type)) {
    createEdgeValidationError(input.type, srcNode.type, 'source');
  }
  if (dstNode && !isValidTargetType(input.type, dstNode.type)) {
    createEdgeValidationError(input.type, dstNode.type, 'target');
  }

  const { data, error } = await supabase
    .from('edges')
    .upsert(input, {
      onConflict: 'tenant_id,type,src,dst',
      ignoreDuplicates: false, // Update existing edge on conflict
    })
    .select()
    .single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'upsert edge',
      entityType: 'edge',
      edgeType: input.type,
    });
  }
  return data as Edge;
}

/**
 * Soft delete an edge (sets deleted_at timestamp)
 */
export async function softDeleteEdge(edgeId: string, client?: SupabaseClient): Promise<Edge> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('edges').update({ deleted_at: new Date().toISOString() }).eq('id', edgeId).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'soft delete edge',
      entityType: 'edge',
    });
  }
  return data as Edge;
}

/**
 * Permanently delete an edge
 */
export async function deleteEdge(edgeId: string, client?: SupabaseClient): Promise<void> {
  const supabase = resolveClient(client);

  const { error } = await supabase.from('edges').delete().eq('id', edgeId);

  if (error) {
    handleSupabaseError(error, {
      operation: 'delete edge',
      entityType: 'edge',
    });
  }
}

/**
 * Restore a soft-deleted edge
 */
export async function restoreEdge(edgeId: string, client?: SupabaseClient): Promise<Edge> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('edges').update({ deleted_at: null }).eq('id', edgeId).select().single();

  if (error) {
    handleSupabaseError(error, {
      operation: 'restore edge',
      entityType: 'edge',
    });
  }
  return data as Edge;
}

// ============================================
// GRAPH TRAVERSAL OPERATIONS
// ============================================

/**
 * Get outgoing edges for a node
 */
export async function getOutgoingEdges(nodeId: string, edgeType?: string, client?: SupabaseClient): Promise<Edge[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('edges').select('*').eq('src', nodeId).is('deleted_at', null);

  if (edgeType) {
    query = query.eq('type', edgeType);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'get outgoing edges',
      entityType: 'edge',
    });
  }
  return data as Edge[];
}

/**
 * Get incoming edges for a node
 */
export async function getIncomingEdges(nodeId: string, edgeType?: string, client?: SupabaseClient): Promise<Edge[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('edges').select('*').eq('dst', nodeId).is('deleted_at', null);

  if (edgeType) {
    query = query.eq('type', edgeType);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'get incoming edges',
      entityType: 'edge',
    });
  }
  return data as Edge[];
}

/**
 * Get a node with all its relationships
 */
export async function getNodeWithRelationships(nodeId: string, client?: SupabaseClient): Promise<NodeWithRelationships> {
  const supabase = resolveClient(client);

  // Use optimized RPC function that fetches everything in 1 query instead of 5
  const { data, error } = await supabase.rpc('get_node_with_relationships', {
    p_node_id: nodeId,
  });

  if (error) {
    handleSupabaseError(error, {
      operation: 'get node with relationships',
      entityType: 'node',
    });
  }
  if (!data) {
    throw new GraphNotFoundError(`Node with id ${nodeId} not found`, GraphErrorCode.NOT_FOUND, {
      entityId: nodeId,
    });
  }

  // Transform the JSONB result to match the expected TypeScript interface
  return {
    node: data.node as Node,
    outgoing: (data.outgoing || []) as Array<{ edge: Edge; targetNode: Node }>,
    incoming: (data.incoming || []) as Array<{ edge: Edge; sourceNode: Node }>,
  };
}

/**
 * Get PMN (Properties, Metrics, Narrative) data for a node in a single optimized query
 * Replaces 8 queries (4 for edges + 4 for nodes) with 1 query
 */
export async function getPMNData(
  nodeId: string,
  client?: SupabaseClient
): Promise<{
  properties: Array<{ edge: Edge; node: Node }>;
  metrics: Array<{ edge: Edge; node: Node }>;
  narrative: Array<{ edge: Edge; node: Node }>;
}> {
  const supabase = resolveClient(client);

  // Use optimized RPC function that fetches all PMN data in 1 query
  const { data, error } = await supabase.rpc('get_pmn_data', {
    p_node_id: nodeId,
  });

  if (error) {
    handleSupabaseError(error, {
      operation: 'get PMN data',
      entityType: 'node',
    });
  }

  // Transform database result to expected format
  const result = {
    properties: [] as Array<{ edge: Edge; node: Node }>,
    metrics: [] as Array<{ edge: Edge; node: Node }>,
    narrative: [] as Array<{ edge: Edge; node: Node }>,
  };

  // Map edge types to result fields
  (data || []).forEach((row: { edge_type: string; items: Array<{ edge: Edge; node: Node }> }) => {
    const items = row.items || [];
    switch (row.edge_type) {
      case 'HAS_PROPERTY':
        result.properties = items;
        break;
      case 'HAS_METRIC':
        result.metrics = items;
        break;
      case 'HAS_NARRATIVE':
        result.narrative = items;
        break;
    }
  });

  return result;
}

/**
 * Search nodes by JSONB property
 */
export async function searchNodesByProp(tenantId: string, type: string, propKey: string, propValue: unknown, client?: SupabaseClient): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('nodes')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('type', type)
    .contains('props', { [propKey]: propValue })
    .is('deleted_at', null);

  if (error) {
    handleSupabaseError(error, {
      operation: 'search nodes by property',
      entityType: 'node',
      nodeType: type,
    });
  }
  return data as Node[];
}

/**
 * Find organizations by location using direct JSONB queries
 * This function queries nested geo properties (city, state, country) directly
 * without relying on search_vector, making it work immediately for exact matches.
 */
export async function findOrganizationsByLocation(
  filters: {
    city?: string;
    state?: string;
    country?: string;
  },
  options?: {
    tenantId?: string;
    limit?: number;
  },
  client?: SupabaseClient
): Promise<Node[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('nodes').select('*').eq('type', 'Organization').is('deleted_at', null);

  if (options?.tenantId) {
    query = query.eq('tenant_id', options.tenantId);
  }

  // Direct JSONB path queries for nested geo fields
  if (filters.city) {
    query = query.eq('props->geo->>city', filters.city);
  }

  if (filters.state) {
    query = query.eq('props->geo->>state', filters.state);
  }

  if (filters.country) {
    query = query.eq('props->geo->>country', filters.country);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'find organizations by location',
      entityType: 'node',
      nodeType: 'Organization',
    });
  }

  return data as Node[];
}

/**
 * Find people by location using direct JSONB queries
 * This function queries nested geo properties (city, state, country) directly
 * without relying on search_vector, making it work immediately for exact matches.
 */
export async function findPeopleByLocation(
  filters: {
    city?: string;
    state?: string;
    country?: string;
  },
  options?: {
    tenantId?: string;
    limit?: number;
  },
  client?: SupabaseClient
): Promise<Node[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('nodes').select('*').eq('type', 'Person').is('deleted_at', null);

  if (options?.tenantId) {
    query = query.eq('tenant_id', options.tenantId);
  }

  // Direct JSONB path queries for nested geo fields
  if (filters.city) {
    query = query.eq('props->geo->>city', filters.city);
  }

  if (filters.state) {
    query = query.eq('props->geo->>state', filters.state);
  }

  if (filters.country) {
    query = query.eq('props->geo->>country', filters.country);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) {
    handleSupabaseError(error, {
      operation: 'find people by location',
      entityType: 'node',
      nodeType: 'Person',
    });
  }

  return data as Node[];
}

/**
 * Count nodes by type
 */
export async function countNodesByType(tenantId: string, type: string, client?: SupabaseClient): Promise<number> {
  const supabase = resolveClient(client);

  const { count, error } = await supabase.from('nodes').select('*', { count: 'exact', head: true }).eq('tenant_id', tenantId).eq('type', type).is('deleted_at', null);

  if (error) {
    handleSupabaseError(error, {
      operation: 'count nodes by type',
      entityType: 'node',
      nodeType: type,
    });
  }
  return count || 0;
}

/**
 * Count edges by type
 */
export async function countEdgesByType(tenantId: string, type: string, client?: SupabaseClient): Promise<number> {
  const supabase = resolveClient(client);

  const { count, error } = await supabase.from('edges').select('*', { count: 'exact', head: true }).eq('tenant_id', tenantId).eq('type', type).is('deleted_at', null);

  if (error) {
    handleSupabaseError(error, {
      operation: 'count edges by type',
      entityType: 'edge',
      edgeType: type,
    });
  }
  return count || 0;
}

export interface GraphSearchResult {
  id: string;
  type: string;
  props: Record<string, unknown>;
  natural_key: string | null;
  created_at: string;
  match_source: 'props' | 'amfi';
  parent_id: string | null;
  parent_type: string | null;
}

/**
 * Search nodes across types with text matching (uses RPC with fuzzy search)
 */
export async function searchNodes(
  query: string,
  options?: {
    types?: string[];
    includeDeleted?: boolean;
    limit?: number;
    tenantId?: string;
    page?: number;
    withRank?: boolean; // Return rank for relevance sorting
  },
  client?: SupabaseClient
): Promise<Node[]> {
  const supabase = resolveClient(client);
  const limit = options?.limit || 20;
  const page = options?.page || 1;

  // Get allowed types if tenant specified (for module permissions)
  let allowedTypes = options?.types;
  if (options?.tenantId) {
    const moduleConfig = await getTenantModuleConfig(options.tenantId, client);
    const enabledTypes = Object.entries(moduleConfig)
      .filter(([_, config]) => config.enabled)
      .flatMap(([_, config]) => config.node_types || []);

    // Intersect requested types with enabled types
    if (allowedTypes) {
      allowedTypes = allowedTypes.filter((t) => enabledTypes.includes(t));
    } else {
      allowedTypes = enabledTypes;
    }

    if (allowedTypes.length === 0) {
      return []; // No enabled types to search
    }
  }

  const nodeType = allowedTypes?.length === 1 ? allowedTypes[0] : null;

  // Use optimized FTS search function
  const { data, error } = await supabase.rpc('search_graph_nodes_fts', {
    search_term: query,
    node_type: nodeType,
    allowed_types: allowedTypes && allowedTypes.length > 1 ? allowedTypes : null,
    tenant_id_filter: options?.tenantId || null,
    page_num: page,
    page_size: limit,
  });

  if (error) {
    handleSupabaseError(error, {
      operation: 'search nodes',
      entityType: 'node',
    });
  }

  // Return results with or without rank based on option
  return (data || []) as Node[];
}

/**
 * Global search across all node types (uses optimized FTS with relevance ranking)
 */
export async function globalSearch(
  query: string,
  options?: {
    page?: number;
    pageSize?: number;
    tenantId?: string;
  },
  client?: SupabaseClient
): Promise<GraphSearchResult[]> {
  // Use the updated searchNodes function which now uses FTS
  const results = await searchNodes(
    query,
    {
      types: undefined, // Search all types
      page: options?.page,
      limit: options?.pageSize,
      tenantId: options?.tenantId,
    },
    client
  );

  return results as unknown as GraphSearchResult[];
}

/**
 * List edges with pagination support
 */
export async function listEdges(
  type: string,
  filters?: {
    src?: string;
    dst?: string;
    includeDeleted?: boolean;
  },
  client?: SupabaseClient
): Promise<Edge[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('edges').select('*').eq('type', type);

  if (filters?.src) {
    query = query.eq('src', filters.src);
  }

  if (filters?.dst) {
    query = query.eq('dst', filters.dst);
  }

  if (!filters?.includeDeleted) {
    query = query.is('deleted_at', null);
  }

  const { data, error } = await query;
  if (error) {
    handleSupabaseError(error, {
      operation: 'list edges',
      entityType: 'edge',
      edgeType: type,
    });
  }

  return data as Edge[];
}

// ============================================
// ADVANCED GRAPH TRAVERSAL
// ============================================

export interface NeighborsResult {
  neighbors: Node[];
  edges: Edge[];
}

/**
 * Get all neighboring nodes connected by specific edge type
 */
export async function getNeighbors(nodeId: string, edgeType: string, direction: 'outgoing' | 'incoming' | 'both' = 'both', client?: SupabaseClient): Promise<NeighborsResult> {
  const supabase = resolveClient(client);

  // Get edges
  let edgeQuery = supabase.from('edges').select('*').eq('type', edgeType).is('deleted_at', null);

  if (direction === 'outgoing') {
    edgeQuery = edgeQuery.eq('src', nodeId);
  } else if (direction === 'incoming') {
    edgeQuery = edgeQuery.eq('dst', nodeId);
  } else {
    edgeQuery = edgeQuery.or(`src.eq.${nodeId},dst.eq.${nodeId}`);
  }

  const { data: edges, error: edgesError } = await edgeQuery;

  if (edgesError) {
    handleSupabaseError(edgesError, {
      operation: 'get neighbor edges',
      entityType: 'edge',
      edgeType,
    });
  }

  if (!edges || edges.length === 0) {
    return { neighbors: [], edges: [] };
  }

  // Get neighbor node IDs
  const neighborIds = edges.map((e: Edge) => (e.src === nodeId ? e.dst : e.src)).filter((id, index, self) => self.indexOf(id) === index); // Unique

  // Get nodes
  const { data: nodes, error: nodesError } = await supabase.from('nodes').select('*').in('id', neighborIds).is('deleted_at', null);

  if (nodesError) {
    handleSupabaseError(nodesError, {
      operation: 'get neighbor nodes',
      entityType: 'node',
    });
  }

  return {
    neighbors: (nodes as Node[]) || [],
    edges: edges as Edge[],
  };
}

/**
 * Get all neighbors regardless of edge type
 */
export async function getAllNeighbors(nodeId: string, direction: 'outgoing' | 'incoming' | 'both' = 'both', client?: SupabaseClient): Promise<NeighborsResult> {
  const supabase = resolveClient(client);

  let edgeQuery = supabase.from('edges').select('*').is('deleted_at', null);

  if (direction === 'outgoing') {
    edgeQuery = edgeQuery.eq('src', nodeId);
  } else if (direction === 'incoming') {
    edgeQuery = edgeQuery.eq('dst', nodeId);
  } else {
    edgeQuery = edgeQuery.or(`src.eq.${nodeId},dst.eq.${nodeId}`);
  }

  const { data: edges, error: edgesError } = await edgeQuery;

  if (edgesError) {
    handleSupabaseError(edgesError, {
      operation: 'get all neighbor edges',
      entityType: 'edge',
    });
  }

  if (!edges || edges.length === 0) {
    return { neighbors: [], edges: [] };
  }

  const neighborIds = edges.map((e: Edge) => (e.src === nodeId ? e.dst : e.src)).filter((id, index, self) => self.indexOf(id) === index);

  const { data: nodes, error: nodesError } = await supabase.from('nodes').select('*').in('id', neighborIds).is('deleted_at', null);

  if (nodesError) {
    handleSupabaseError(nodesError, {
      operation: 'get all neighbor nodes',
      entityType: 'node',
    });
  }

  return {
    neighbors: (nodes as Node[]) || [],
    edges: edges as Edge[],
  };
}

export interface TraversalResult {
  nodes: Node[];
  edges: Edge[];
  depth: number;
}

/**
 * Breadth-first traversal from a node up to max depth
 */
export async function traverseBFS(startNodeId: string, maxDepth: number = 3, edgeTypeFilter?: string, client?: SupabaseClient): Promise<TraversalResult> {
  const supabase = resolveClient(client);

  // Use optimized RPC function with recursive CTE
  // This replaces ~468 sequential queries with 1 query for depth-3 traversal
  const { data, error } = await supabase.rpc('traverse_bfs', {
    p_start_node_id: startNodeId,
    p_max_depth: maxDepth,
    p_edge_type_filter: edgeTypeFilter || null,
  });

  if (error) {
    handleSupabaseError(error, {
      operation: 'traverse BFS',
      entityType: 'node',
    });
  }

  // Handle case where node doesn't exist
  if (!data || (data.nodes.length === 0 && data.edges.length === 0)) {
    return {
      nodes: [],
      edges: [],
      depth: maxDepth,
    };
  }

  return {
    nodes: data.nodes || [],
    edges: data.edges || [],
    depth: maxDepth,
  };
}

// ============================================
// GRAPH ANALYTICS
// ============================================

export interface NodeDegree {
  inDegree: number;
  outDegree: number;
  totalDegree: number;
}

/**
 * Get node degree (number of connections)
 */
export async function getNodeDegree(nodeId: string, client?: SupabaseClient): Promise<NodeDegree> {
  const supabase = resolveClient(client);

  const [outgoing, incoming] = await Promise.all([
    supabase.from('edges').select('id', { count: 'exact', head: true }).eq('src', nodeId).is('deleted_at', null),
    supabase.from('edges').select('id', { count: 'exact', head: true }).eq('dst', nodeId).is('deleted_at', null),
  ]);

  const outDegree = outgoing.count || 0;
  const inDegree = incoming.count || 0;

  return {
    inDegree,
    outDegree,
    totalDegree: inDegree + outDegree,
  };
}

/**
 * Get edge count by type for a node
 */
export async function getEdgeTypeDistribution(nodeId: string, client?: SupabaseClient): Promise<Record<string, number>> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('edges').select('type').or(`src.eq.${nodeId},dst.eq.${nodeId}`).is('deleted_at', null);

  if (error) {
    handleSupabaseError(error, {
      operation: 'get edge type distribution',
      entityType: 'edge',
    });
  }

  const distribution: Record<string, number> = {};
  (data || []).forEach((edge: { type: string }) => {
    distribution[edge.type] = (distribution[edge.type] || 0) + 1;
  });

  return distribution;
}

// ============================================
// BULK OPERATIONS
// ============================================

export interface BulkCreateResult<T> {
  created: T[];
  errors: Array<{ index: number; error: string; input: unknown }>;
}

export interface BulkDeleteResult {
  deleted: number;
  errors: Array<{ id: string; error: string }>;
}

/**
 * Bulk create nodes (continues on error)
 */
export async function bulkCreateNodes<TProps = Record<string, unknown>>(
  inputs: CreateNodeInput<TProps>[],
  options?: {
    client?: SupabaseClient;
    onConflict?: 'ignore' | 'update' | 'error';
    chunkSize?: number;
  }
): Promise<BulkCreateResult<Node>> {
  const supabase = resolveClient(options?.client);
  const created: Node[] = [];
  const errors: Array<{ index: number; error: string; input: unknown }> = [];
  const onConflict = options?.onConflict || 'error';
  const chunkSize = options?.chunkSize || 100;

  // Validate all inputs first
  const validatedInputs: Array<{
    index: number;
    input: CreateNodeInput<TProps>;
    validatedProps: unknown;
  }> = [];

  inputs.forEach((input, index) => {
    const schema = NodeSchemaRegistry[input.type];
    if (!schema) {
      errors.push({
        index,
        error: `Invalid node type: ${input.type}`,
        input,
      });
      return;
    }

    const validation = validateNodeProps(schema, input.props);
    if (!validation.success) {
      errors.push({
        index,
        error: validation.errors.errors.map((e: { message: string }) => e.message).join(', '),
        input,
      });
      return;
    }

    validatedInputs.push({
      index,
      input,
      validatedProps: validation.data,
    });
  });

  // Process in chunks to isolate errors
  if (validatedInputs.length > 0) {
    // Use upsert for conflict handling (ignore or update)
    if (onConflict === 'ignore' || onConflict === 'update') {
      // Process all at once with upsert
      const records = validatedInputs.map((v) => ({
        ...v.input,
        props: v.validatedProps,
      }));

      const { data, error } = await supabase
        .from('nodes')
        .upsert(records, {
          onConflict: 'tenant_id,type,natural_key',
          ignoreDuplicates: onConflict === 'ignore',
        })
        .select();

      if (error) {
        // If entire batch fails, mark all as errors
        // Convert Supabase error to GraphError for consistent error handling
        try {
          handleSupabaseError(error, {
            operation: 'bulk create nodes',
            entityType: 'node',
          });
        } catch (e) {
          const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
          validatedInputs.forEach((v) => {
            errors.push({
              index: v.index,
              error: errorMessage,
              input: v.input,
            });
          });
        }
      } else {
        created.push(...((data as Node[]) || []));
      }
    } else {
      // Default 'error' mode: Use chunked inserts
      for (let i = 0; i < validatedInputs.length; i += chunkSize) {
        const chunk = validatedInputs.slice(i, i + chunkSize);
        const records = chunk.map((v) => ({
          ...v.input,
          props: v.validatedProps,
        }));

        const { data, error } = await supabase.from('nodes').insert(records).select();

        if (error) {
          // Only this chunk failed - mark all items in chunk as errors
          // Convert Supabase error to GraphError for consistent error handling
          try {
            handleSupabaseError(error, {
              operation: 'bulk create nodes',
              entityType: 'node',
            });
          } catch (e) {
            const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
            chunk.forEach((v) => {
              errors.push({
                index: v.index,
                error: errorMessage,
                input: v.input,
              });
            });
          }
        } else {
          created.push(...((data as Node[]) || []));
        }
      }
    }
  }

  return { created, errors };
}

/**
 * Bulk update nodes by IDs
 */
export async function bulkUpdateNodes<TProps = Record<string, unknown>>(updates: Array<{ id: string; props: TProps }>, client?: SupabaseClient): Promise<BulkCreateResult<Node>> {
  const supabase = resolveClient(client);
  const created: Node[] = [];
  const errors: Array<{ index: number; error: string; input: unknown }> = [];

  for (let i = 0; i < updates.length; i++) {
    const update = updates[i];
    if (!update) continue;

    const { id, props } = update;

    try {
      // Get existing node
      const { data: existing, error: fetchError } = await supabase.from('nodes').select('*').eq('id', id).is('deleted_at', null).single();

      if (fetchError || !existing) {
        errors.push({
          index: i,
          error: 'Node not found',
          input: updates[i],
        });
        continue;
      }

      // Merge and validate
      const schema = NodeSchemaRegistry[existing.type];
      const mergedProps = { ...(existing as Node).props, ...props };

      if (schema) {
        const validation = validateNodeProps(schema, mergedProps);
        if (!validation.success) {
          errors.push({
            index: i,
            error: validation.errors.errors.map((e: { message: string }) => e.message).join(', '),
            input: updates[i],
          });
          continue;
        }
      }

      // Update
      const { data: updated, error: updateError } = await supabase.from('nodes').update({ props: mergedProps }).eq('id', id).select().single();

      if (updateError) {
        // Convert Supabase error to GraphError for consistent error handling
        try {
          handleSupabaseError(updateError, {
            operation: 'bulk update nodes',
            entityType: 'node',
          });
        } catch (e) {
          errors.push({
            index: i,
            error: isGraphError(e) ? JSON.stringify(e.toJSON()) : updateError.message,
            input: updates[i],
          });
        }
      } else {
        created.push(updated as Node);
      }
    } catch (e: unknown) {
      errors.push({
        index: i,
        error: isGraphError(e) ? JSON.stringify(e.toJSON()) : e instanceof Error ? e.message : 'Unknown error',
        input: updates[i],
      });
    }
  }

  return { created, errors };
}

/**
 * Bulk delete nodes (soft delete by default)
 */
export async function bulkDeleteNodes(nodeIds: string[], soft: boolean = true, client?: SupabaseClient): Promise<BulkDeleteResult> {
  const supabase = resolveClient(client);
  let deleted = 0;
  const errors: Array<{ id: string; error: string }> = [];

  if (soft) {
    const { data, error } = await supabase.from('nodes').update({ deleted_at: new Date().toISOString() }).in('id', nodeIds).is('deleted_at', null).select();

    if (error) {
      // Convert Supabase error to GraphError for consistent error handling
      try {
        handleSupabaseError(error, {
          operation: 'bulk delete nodes',
          entityType: 'node',
        });
      } catch (e) {
        const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
        nodeIds.forEach((id) => errors.push({ id, error: errorMessage }));
      }
    } else {
      deleted = (data as Node[])?.length || 0;
    }
  } else {
    const { data, error } = await supabase.from('nodes').delete().in('id', nodeIds).select();

    if (error) {
      // Convert Supabase error to GraphError for consistent error handling
      try {
        handleSupabaseError(error, {
          operation: 'bulk delete nodes',
          entityType: 'node',
        });
      } catch (e) {
        const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
        nodeIds.forEach((id) => errors.push({ id, error: errorMessage }));
      }
    } else {
      deleted = (data as Node[])?.length || 0;
    }
  }

  return { deleted, errors };
}

/**
 * Bulk create edges
 */
export async function bulkCreateEdges<TProps = Record<string, unknown>>(inputs: CreateEdgeInput<TProps>[], client?: SupabaseClient): Promise<BulkCreateResult<Edge>> {
  const supabase = resolveClient(client);
  const created: Edge[] = [];
  const errors: Array<{ index: number; error: string; input: unknown }> = [];

  // Batch insert
  const { data, error } = await supabase.from('edges').insert(inputs).select();

  if (error) {
    // If bulk insert fails, fall back to individual inserts
    for (let i = 0; i < inputs.length; i++) {
      try {
        const { data: edge, error: edgeError } = await supabase.from('edges').insert(inputs[i]).select().single();

        if (edgeError) {
          // Convert Supabase error to GraphError for consistent error handling
          try {
            handleSupabaseError(edgeError, {
              operation: 'bulk create edges',
              entityType: 'edge',
            });
          } catch (e) {
            errors.push({
              index: i,
              error: isGraphError(e) ? JSON.stringify(e.toJSON()) : edgeError.message,
              input: inputs[i],
            });
          }
        } else {
          created.push(edge as Edge);
        }
      } catch (e: unknown) {
        errors.push({
          index: i,
          error: isGraphError(e) ? JSON.stringify(e.toJSON()) : e instanceof Error ? e.message : 'Unknown error',
          input: inputs[i],
        });
      }
    }
  } else {
    created.push(...((data as Edge[]) || []));
  }

  return { created, errors };
}

/**
 * Bulk delete edges (soft delete by default)
 */
export async function bulkDeleteEdges(edgeIds: string[], soft: boolean = true, client?: SupabaseClient): Promise<BulkDeleteResult> {
  const supabase = resolveClient(client);
  let deleted = 0;
  const errors: Array<{ id: string; error: string }> = [];

  if (soft) {
    const { data, error } = await supabase.from('edges').update({ deleted_at: new Date().toISOString() }).in('id', edgeIds).is('deleted_at', null).select();

    if (error) {
      // Convert Supabase error to GraphError for consistent error handling
      try {
        handleSupabaseError(error, {
          operation: 'bulk delete edges',
          entityType: 'edge',
        });
      } catch (e) {
        const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
        edgeIds.forEach((id) => errors.push({ id, error: errorMessage }));
      }
    } else {
      deleted = (data as Edge[])?.length || 0;
    }
  } else {
    const { data, error } = await supabase.from('edges').delete().in('id', edgeIds).select();

    if (error) {
      // Convert Supabase error to GraphError for consistent error handling
      try {
        handleSupabaseError(error, {
          operation: 'bulk delete edges',
          entityType: 'edge',
        });
      } catch (e) {
        const errorMessage = isGraphError(e) ? JSON.stringify(e.toJSON()) : error.message;
        edgeIds.forEach((id) => errors.push({ id, error: errorMessage }));
      }
    } else {
      deleted = (data as Edge[])?.length || 0;
    }
  }

  return { deleted, errors };
}

// ============================================
// ENHANCED FULL-TEXT SEARCH
// ============================================

/**
 * Enhanced full-text search across nodes using PostgreSQL text search
 * Searches in both natural_key and stringified props
 */
export async function searchNodesFullText(
  query: string,
  options: {
    nodeTypes?: string[];
    page?: number;
    pageSize?: number;
    includeDeleted?: boolean;
  } = {},
  client?: SupabaseClient
): Promise<PaginatedResponse<Node>> {
  const supabase = resolveClient(client);
  const { nodeTypes, page = 1, pageSize = 50, includeDeleted = false } = options;

  // Build query with text search
  let dbQuery = supabase.from('nodes').select('*', { count: 'exact' }).textSearch('props', query, {
    type: 'websearch',
    config: 'english',
  });

  if (nodeTypes && nodeTypes.length > 0) {
    dbQuery = dbQuery.in('type', nodeTypes);
  }

  if (!includeDeleted) {
    dbQuery = dbQuery.is('deleted_at', null);
  }

  // Pagination
  const start = (page - 1) * pageSize;
  dbQuery = dbQuery.range(start, start + pageSize - 1);

  const { data, error, count } = await dbQuery;

  if (error) {
    handleSupabaseError(error, {
      operation: 'search nodes full text',
      entityType: 'node',
    });
  }

  const total = count || 0;
  const totalPages = Math.ceil(total / pageSize);

  return {
    data: (data as Node[]) || [],
    pagination: {
      total,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    },
  };
}
